import { useCallback, useEffect, useRef, useState } from 'react'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { io, Socket } from 'socket.io-client'
import { Buffer } from 'buffer'
import { isArray } from 'lodash'

import { Biological } from '@/core/models/consultation.model'
import { consultationOfflineStore, documentOfflineStore, OfflineConsultation } from '@/core/offline'

export default function useOfflineMedconnectDevices(
    isStreamer: boolean,
    consultation: OfflineConsultation,
    onConsultationUpdate: (consultation: OfflineConsultation) => void
) {
    const { enqueueSnackbar } = useSnackbar()
    const { t } = useTranslation()

    const socket = useRef<Socket>()

    const [deviceTracker, setDeviceTracker] = useState<boolean>(false)
    const [status, setPC300Status] = useState<boolean>(false)
    const biologicals: any[] = []   // prevents the same biological from being entered again in case the device is reconnected

    useEffect(() => {
        socket.current = io('http://localhost:4000')

        return () => {
            socket.current?.close()
        }
    }, [])

    const sendFileFunction = useCallback(
        async (response: string[]) => {
            if (!consultation._id) return

            try {
                if (!isArray(response)) {
                    const byteCharacters = Buffer.from(response, 'base64')
                    const base64Data = `data:application/pdf;base64,${response}`

                    // Create offline document
                    await documentOfflineStore.createOfflineDocument({
                        filename: `ecg-${Date.now().toString()}.pdf`,
                        size: byteCharacters.length,
                        mimeType: 'application/pdf',
                        base64Data: base64Data,
                        consultationId: consultation._id
                    })
                } else {
                    // convert base64 to blob
                    const byteCharacters = Buffer.from(response[0], 'base64')
                    const getTypeFromFileSuffix = (fileSuffix: string) => {
                        switch (fileSuffix.toLowerCase()) {
                            case 'pdf':
                                return 'application/pdf'
                            case 'jpg':
                                return 'image/jpg'
                            case 'jpeg':
                                return 'image/jpeg'
                            case 'png':
                                return 'image/png'
                            case 'mp4':
                                return 'video/mp4'
                            default:
                                return 'application/pdf'
                        }
                    }

                    const mimeType = getTypeFromFileSuffix(response[2] ?? 'pdf')
                    const base64Data = `data:${mimeType};base64,${response[0]}`

                    await documentOfflineStore.createOfflineDocument({
                        filename: `${response[1]}-${Date.now().toString()}.${response[2] ?? 'pdf'}`,
                        size: byteCharacters.length,
                        mimeType: mimeType,
                        base64Data: base64Data,
                        consultationId: consultation._id
                    })
                }

                enqueueSnackbar(t('notification.document.create.success'), { variant: 'success' })
                setDeviceTracker(false)

                // Refresh consultation data
                const updatedConsultation = await consultationOfflineStore.get(consultation._id)
                if (updatedConsultation) {
                    onConsultationUpdate(updatedConsultation)
                }
            } catch (error) {
                enqueueSnackbar(t('notification.document.create.error'), { variant: 'error' })
            }
        },
        [consultation._id, enqueueSnackbar, t, onConsultationUpdate]
    )

    const sendBiologicalFunction = useCallback(
        async (item: Biological) => {
            if (!consultation._id) return

            try {
                const updatedConsultation = await consultationOfflineStore.addBiological(consultation._id, {
                    ...item,
                    comment: '',
                    files: []
                })

                biologicals.push({ ...item })
                onConsultationUpdate(updatedConsultation)
                enqueueSnackbar(t('notification.localConsultation.biological.create.success'), { variant: 'success' })
                console.log("biological created successfully")
            } catch (error) {
                enqueueSnackbar(t('notification.localConsultation.biological.create.error'), { variant: 'error' })
            }
        },
        [consultation._id, enqueueSnackbar, t, onConsultationUpdate]
    )

    const sendMesuresFunction = useCallback(
        async (mesures: any[]) => {
            if (!consultation._id) return

            try {
                const updatedConsultation = await consultationOfflineStore.updateConsultation(consultation._id, {
                    mesures
                })

                onConsultationUpdate(updatedConsultation)
                enqueueSnackbar(t('notification.consultation.update.success'), { variant: 'success' })
            } catch (error) {
                enqueueSnackbar(t('notification.consultation.update.error'), { variant: 'error' })
            }
        },
        [consultation._id, enqueueSnackbar, t, onConsultationUpdate]
    )

    const isBiologicalExisting = (item: Biological) => {
        return biologicals.some(biological =>
            biological.label === item.label &&
            biological.unit === item.unit &&
            biological.value === item.value
        );
    }

    useEffect(() => {
        const listener = (event: MessageEvent) => {
            if (event.data.type === 'PC300') {
                console.log('received PC300', event.data.PC300)
                sendMesuresFunction([event.data.PC300])
            } else if (event.data.type === 'biological') {
                console.log('received biological', event.data.biological)
                if (!isBiologicalExisting(event.data.biological)) {
                    console.log('biological not existing yet')
                    sendBiologicalFunction(event.data.biological)
                } else {
                    console.log('Biological already existing !!!!!')
                }
            }
        }

        window.addEventListener('message', listener)

        return () => {
            window.removeEventListener('message', listener)
        }
    }, [sendMesuresFunction, sendBiologicalFunction])

    useEffect(() => {
        if (!isStreamer || !socket.current) return

        socket.current.on('PC300', sendMesuresFunction)
        socket.current.on('PC300status', (status: boolean) => setPC300Status(status))
        socket.current.on('biological', sendBiologicalFunction)
        socket.current.on('file', sendFileFunction)

        return () => {
            if (!socket.current) return
            socket.current.off('biological')
            socket.current.off('file')
            socket.current.off('PC300')
            socket.current.off('PC300status')
        }
    }, [isStreamer, sendMesuresFunction, sendBiologicalFunction, sendFileFunction])

    return {
        setDeviceTracker,
        deviceTracker,
        status,
    }
} 